import { useState, useMemo, useEffect } from 'react';
import { Transaction, Budget } from '../types/budget';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateBudget } from '../db';
import { filterTransactions, sortTransactions } from '../utils/transactionCalculations';
import { useTransactionFilters } from './useTransactionFilters';

interface UseTransactionsProps {
  budget: Budget;
}

export const useTransactions = ({ budget }: UseTransactionsProps) => {
  const {
    filters,
    setFilters,
    sortField,
    sortDirection,
    setSort,
    hasActiveFilters,
  } = useTransactionFilters();
  
  const [showFilters, setShowFilters] = useState(hasActiveFilters);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction>();

  // Auto-show filters if there are active filters from URL
  useEffect(() => {
    if (hasActiveFilters && !showFilters) {
      setShowFilters(true);
    }
  }, [hasActiveFilters, showFilters]);

  const queryClient = useQueryClient();

  // Define the budget-specific query key
  const budgetId = budget.id;

  const updateBudgetMutation = useMutation({
    mutationFn: async (updatedBudget: Budget) => {
      updateBudget(updatedBudget);
      return updatedBudget;
    },
    onSuccess: () => {
      // Invalidate the specific budget query if we have an ID
      if (budgetId) {
        queryClient.invalidateQueries({ queryKey: ['budget', budgetId] });
      }

      // Invalidate general queries that might be affected
      queryClient.invalidateQueries({ queryKey: ['defaultBudget'] });
      queryClient.invalidateQueries({ queryKey: ['budgets'] });

      // No need to invalidate a transactions query since transactions are part of budgets
    },
  });

  const handleAddTransaction = (transactions: Array<Omit<Transaction, 'id'>>) => {
    const maxId = budget.transactions.reduce((max, t) => {
      const currentId = t.id ?? 0;
      return Math.max(max, currentId);
    }, 0);

    const newTransactions: Transaction[] = transactions.map((transaction, index) => ({
      ...transaction,
      id: maxId + index + 1,
    }));

    const newBudget = {
      ...budget,
      transactions: [...budget.transactions, ...newTransactions],
    };

    updateBudgetMutation.mutate(newBudget);
    setIsFormOpen(false);
  };

  const handleEditTransaction = (transactions: Array<Omit<Transaction, 'id'>>) => {
    if (!selectedTransaction) return;

    const transaction = transactions[0];
    const newBudget = {
      ...budget,
      transactions: budget.transactions.map((t) =>
        t.id === selectedTransaction.id ? { ...transaction, id: t.id } : t
      ),
    };

    updateBudgetMutation.mutate(newBudget);
    setSelectedTransaction(undefined);
    setIsFormOpen(false);
  };

  const handleDeleteTransaction = (transaction: Transaction) => {
    if (transaction.id === undefined) {
      console.error('Cannot delete transaction without ID');
      return;
    }

    const newBudget = {
      ...budget,
      transactions: budget.transactions.filter((t) => t.id !== transaction.id),
    };

    updateBudgetMutation.mutate(newBudget);
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSort(field, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(field, 'desc');
    }
  };

  const filteredSortedTransactions = useMemo(() => {
    let filtered = budget.transactions;

    // Apply search filter first
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.category.toLowerCase().includes(searchLower) ||
          t.responsible.toLowerCase().includes(searchLower) ||
          t.description.toLowerCase().includes(searchLower) ||
          t.destination.toLowerCase().includes(searchLower) ||
          t.amount.toString().includes(filters.search)
      );
    }

    // Apply other filters
    filtered = filterTransactions(filtered, {
      ...(filters.category ? { category: filters.category } : {}),
      ...(filters.responsible ? { responsible: filters.responsible } : {}),
      ...(filters.destination ? { destination: filters.destination } : {}),
      ...(filters.amountType ? { amountType: filters.amountType as 'income' | 'expense' } : {}),
    });

    return sortTransactions(filtered, sortField as keyof Transaction, sortDirection);
  }, [budget.transactions, filters, sortField, sortDirection]);

  return {
    transactions: filteredSortedTransactions,
    filters,
    setFilters,
    showFilters,
    setShowFilters,
    isFormOpen,
    setIsFormOpen,
    selectedTransaction,
    setSelectedTransaction,
    sortField,
    sortDirection,
    handleSort,
    handleAddTransaction,
    handleEditTransaction,
    handleDeleteTransaction,
  };
};
